import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import config from "../config.js";
import { fetchData, resolveDbUserId } from "../utils/api.js";
import { ToggleChips } from "./common/ToggleChips.js";
import {
  BackIcon,
  GendersIcon,
  DivisionIcon,
  CrosshairsIcon,
} from "./common/Icons.js";
import { initMapKit, initMapKitAsync, searchPlaces } from "../utils/mapkit.js";
import { Button } from "./ui/Button";

const assignCreatorAdminRole = async (newLeagueId, user) => {
  if (!user || !newLeagueId) return;
  try {
    const firebaseUid = user.firebaseUid || user.id || user.uid;
    const targetUserId = await resolveDbUserId(user, firebaseUid);
    if (targetUserId && !Number.isNaN(Number(targetUserId))) {
      await fetchData("/league_roles", {
        method: "POST",
        body: JSON.stringify({
          league_id: newLeagueId,
          user_id: Number(targetUserId),
          role: "Admin",
        }),
      });
    }
  } catch (rErr) {
    console.warn("Failed to create creator admin role:", rErr);
  }
};

const CreateLeagueWizard = ({ onClose, onComplete, user }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // Form State
  const [name, setName] = useState("");
  const [sport, setSport] = useState("softball");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [genders, setGenders] = useState([]);
  const [customDivision, setCustomDivision] = useState("");

  // MapKit Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isMapKitLoaded, setIsMapKitLoaded] = useState(false);
  const searchTimeoutRef = useRef(null);

  const steps = [
    { title: "Basics" },
    { title: "Region" },
    { title: "Registration" },
    { title: "Review" },
  ];

  // Initialize MapKit
  useEffect(() => {
    let isMounted = true;
    const syncLoaded = initMapKit(config.mapKitToken);
    if (syncLoaded || globalThis.mapkit) {
      setIsMapKitLoaded(true);
    } else {
      initMapKitAsync(config.mapKitToken).then((loaded) => {
        if (isMounted && (loaded || globalThis.mapkit)) {
          setIsMapKitLoaded(true);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const handleCitySearch = (query) => {
    setSearchQuery(query);
    setCity(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query || !isMapKitLoaded) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      const places = await searchPlaces(query);
      const filtered = (places || []).filter(
        (p) => p.name || p.formattedAddress || p.locality,
      );
      setSearchResults(filtered);
    }, 500);
  };

  const handleSelectCity = (place) => {
    const cityName = place.locality || place.name || "";
    let stateName =
      place.administrativeAreaCode || place.administrativeArea || "";

    if (!stateName && place.formattedAddress) {
      const parts = place.formattedAddress.split(",");
      if (parts.length >= 2) {
        const lastPart = parts[parts.length - 1].trim();
        const words = lastPart.split(" ");
        if (words.length >= 1 && words[0].length === 2) {
          stateName = words[0];
        }
      }
    }

    setCity(cityName);
    setState(stateName);
    const displayVal = stateName ? `${cityName}, ${stateName}` : cityName;
    setSearchQuery(displayVal);
    setSearchResults([]);
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (globalThis.mapkit) {
          const geocoder = new globalThis.mapkit.Geocoder({
            language: "en-US",
          });
          geocoder.reverseLookup(
            new globalThis.mapkit.Coordinate(latitude, longitude),
            (err, data) => {
              if (!err && data.results && data.results.length > 0) {
                const place = data.results[0];
                if (place) {
                  const cityName = place.locality || place.name || "";
                  const stateName =
                    place.administrativeAreaCode ||
                    place.administrativeArea ||
                    "";
                  setCity(cityName);
                  setState(stateName);

                  const displayVal = stateName
                    ? `${cityName}, ${stateName}`
                    : cityName;
                  setSearchQuery(displayVal);
                  setSearchResults([]);
                }
              }
            },
          );
        }
      },
      (error) => {
        console.warn("Geolocation error:", error);
      },
    );
  };

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const payload = {
        name,
        sport,
        description,
        city,
        state,
        divisions,
        genders,
      };

      const result = await fetchData("/leagues", {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(payload),
      });

      if (result && result.length > 0) {
        const newLeague = result[0];
        const newLeagueId = newLeague.league_id || newLeague.id;
        await assignCreatorAdminRole(newLeagueId, user);
        onComplete({ ...newLeague, userRole: "Admin" });
      } else {
        setError("Failed to create league.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while creating the league.");
    } finally {
      setIsCreating(false);
    }
  };

  const canProceed = () => {
    if (stepIndex === 0) return name.trim().length > 0;
    if (stepIndex === 1)
      return city.trim().length > 0 && state.trim().length > 0;
    return true; // Other steps are optional
  };

  return (
    <div className="drawer-overlay-container fade-in">
      <button
        type="button"
        className="drawer-overlay"
        onClick={onClose}
        aria-label="Close"
      />

      <dialog open className="drawer-content drawer-medium">
        <div
          className="drawer-header"
          style={{
            paddingBottom: "1rem",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <h3 className="drawer-title" style={{ margin: 0 }}>
            Create New League
          </h3>
          <button type="button" onClick={onClose} className="drawer-close-btn">
            <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>
              &times;
            </span>
          </button>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            padding: "1rem 2rem 0 2rem",
          }}
        >
          {/* Left Arrow */}
          {stepIndex > 0 ? (
            <button
              type="button"
              aria-label="Back"
              className="icon-btn"
              onClick={() => setStepIndex((i) => i - 1)}
            >
              <BackIcon size="md" />
            </button>
          ) : (
            <div />
          )}

          {/* Right Arrow */}
          {stepIndex < steps.length - 1 && (
            <button
              type="button"
              aria-label="Next"
              className="icon-btn"
              disabled={!canProceed()}
              onClick={() => setStepIndex((i) => i + 1)}
            >
              <BackIcon size="md" style={{ transform: "scaleX(-1)" }} />
            </button>
          )}
        </div>

        <div
          className="wizard-progress"
          style={{
            display: "flex",
            gap: "8px",
            padding: "1rem 2rem",
            background: "var(--color-surface)",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          {steps.map((step, idx) => (
            <div
              key={step.title}
              style={{
                flex: 1,
                height: "4px",
                background:
                  idx <= stepIndex
                    ? "var(--color-turquoise)"
                    : "var(--color-border)",
                borderRadius: "2px",
              }}
            />
          ))}
        </div>

        <div
          className="wizard-body"
          style={{
            padding:
              "1rem 4rem" /* Increased side padding to make room for arrows */,
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {stepIndex === 0 && (
            <div className="form-compact fade-in" style={{ flexGrow: 1 }}>
              <h2 style={{ marginBottom: "1.5rem" }}>
                Let's start with the basics
              </h2>
              <div className="form-group full-width">
                <label htmlFor="leagueName">League Name</label>
                <input
                  id="leagueName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. San Francisco Softball League"
                />
              </div>
              <div
                className="form-group full-width"
                style={{ marginTop: "1rem" }}
              >
                <label htmlFor="leagueSport">Sport</label>
                <select
                  id="leagueSport"
                  className="input-base"
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                >
                  <option value="softball">Softball</option>
                  <option value="baseball">Baseball</option>
                  <option value="kickball">Kickball</option>
                  <option value="volleyball">Volleyball</option>
                  <option value="soccer">Soccer</option>
                  <option value="basketball">Basketball</option>
                </select>
              </div>
              <div
                className="form-group full-width"
                style={{ marginTop: "1rem" }}
              >
                <label htmlFor="leagueDescription">
                  League Description (Optional)
                </label>
                <textarea
                  id="leagueDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell players a bit about the league..."
                  rows={4}
                />
              </div>
            </div>
          )}

          {stepIndex === 1 && (
            <div className="form-compact fade-in" style={{ flexGrow: 1 }}>
              <h2 style={{ marginBottom: "1.5rem" }}>Where are you located?</h2>

              <div
                className="form-group full-width"
                style={{ position: "relative" }}
              >
                <label htmlFor="searchCity">Search City</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    id="searchCity"
                    type="text"
                    placeholder="Search for your city..."
                    value={searchQuery}
                    onChange={(e) => handleCitySearch(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"

                    onClick={handleUseLocation}
                    title="Use my location"
                    type="button"
                  >
                    <CrosshairsIcon />
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div
                    className="location-autocomplete"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "var(--color-surface)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      zIndex: 10,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    {searchResults.map((place) => (
                      <Button
                        type="button"
                        key={place.formattedAddress || place.name}
                        className="location-autocomplete-item"
                        onClick={() => handleSelectCity(place)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSelectCity(place);
                          }
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            color: "var(--color-text-primary)",
                          }}
                        >
                          {place.name}
                        </div>
                        <div
                          style={{
                            fontSize: "0.85rem",
                            color: "var(--color-text-secondary)",
                          }}
                        >
                          {place.formattedAddress}
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row" style={{ marginTop: "1.5rem" }}>
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    required
                    maxLength={2}
                    placeholder="e.g. CA"
                  />
                </div>
              </div>
            </div>
          )}

          {stepIndex === 2 && (
            <div className="form-compact fade-in" style={{ flexGrow: 1 }}>
              <h2 style={{ marginBottom: "1.5rem" }}>Registration Details</h2>

              <div className="form-group full-width">
                <ToggleChips
                  label={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <DivisionIcon size="sm" /> Divisions (Optional)
                    </span>
                  }
                  options={["Bronze", "Silver", "Gold"]}
                  selectedItems={divisions}
                  onToggle={(clicked) => {
                    const current = divisions || [];
                    const updated = current.includes(clicked)
                      ? current.filter((x) => x !== clicked)
                      : [...current, clicked];
                    setDivisions(updated);
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginTop: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="text"
                    className="input-base"
                    placeholder="Custom division..."
                    value={customDivision}
                    onChange={(e) => setCustomDivision(e.target.value)}
                    style={{ maxWidth: "200px" }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (
                          customDivision.trim() &&
                          !divisions.includes(customDivision.trim())
                        ) {
                          setDivisions([...divisions, customDivision.trim()]);
                          setCustomDivision("");
                        }
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    type="button"

                    onClick={() => {
                      if (
                        customDivision.trim() &&
                        !divisions.includes(customDivision.trim())
                      ) {
                        setDivisions([...divisions, customDivision.trim()]);
                        setCustomDivision("");
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div
                className="form-group full-width"
                style={{ marginTop: "1.5rem" }}
              >
                <ToggleChips
                  label={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <GendersIcon size="sm" /> Genders (Optional)
                    </span>
                  }
                  options={["Men's", "Women's", "Coed"]}
                  selectedItems={genders}
                  variant="switch"
                  onToggle={(clicked) => {
                    const current = genders || [];
                    const updated = current.includes(clicked)
                      ? current.filter((x) => x !== clicked)
                      : [...current, clicked];
                    setGenders(updated);
                  }}
                />
              </div>
            </div>
          )}

          {stepIndex === 3 && (
            <div className="form-compact fade-in" style={{ flexGrow: 1 }}>
              <h2 style={{ marginBottom: "1.5rem" }}>Review & Finish</h2>
              <div
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  padding: "1rem",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <p>
                  <strong>Name:</strong> {name}
                </p>
                <p>
                  <strong style={{ textTransform: "capitalize" }}>
                    Sport:
                  </strong>{" "}
                  {sport}
                </p>
                {description && (
                  <p>
                    <strong>Description:</strong> {description}
                  </p>
                )}
                <p>
                  <strong>Location:</strong> {city}, {state}
                </p>
                {divisions.length > 0 && (
                  <p>
                    <strong>Divisions:</strong> {divisions.join(", ")}
                  </p>
                )}
                {genders.length > 0 && (
                  <p>
                    <strong>Genders:</strong> {genders.join(", ")}
                  </p>
                )}
              </div>

              {error && (
                <p className="error-text" style={{ marginTop: "1rem" }}>
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        <div
          className="drawer-footer"
          style={{
            padding: "1rem 2rem",
            borderTop: "1px solid var(--color-border)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {stepIndex === steps.length - 1 && (
            <Button
              variant="primary"
              type="button"

              disabled={isCreating}
              onClick={handleCreate}
            >
              {isCreating ? "Creating..." : "Create League"}
            </Button>
          )}
        </div>
      </dialog>
    </div>
  );
};

CreateLeagueWizard.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  user: PropTypes.object,
};

export default CreateLeagueWizard;
